import React, { useEffect, useMemo, useState } from 'react';
import {
  MapPin, Plus, X, Loader2, ArrowLeft, User, Phone
} from 'lucide-react';
import BottomSheet from '../ui/BottomSheet';
import { useCustomer } from '../../contexts/CustomerContext';
import { MALE_HOSTELS, FEMALE_HOSTELS } from '../../data/hostelData';

const LABEL_SUGGESTIONS = ['Hostel Room', 'Home', 'Friend', 'Other'];

/* ── Address card — tapping directly selects ── */
function AddressCard({ address, selected, onSelect, profile }) {
  const displayName = address.recipient_name || (address.address_type !== 'other' ? profile?.name : null);
  const displayPhone = address.recipient_phone || (address.address_type !== 'other' ? profile?.phone : null);

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left rounded-2xl px-4 py-3.5 transition-all active:scale-[0.98]
        ${selected
          ? 'bg-black text-white shadow-[0_6px_20px_-6px_rgba(0,0,0,0.5)]'
          : 'bg-gray-50 text-gray-800 shadow-[0_2px_8px_-4px_rgba(0,0,0,0.12)] hover:bg-gray-100'}`}
    >
      <span className="flex items-center gap-2">
        <span className="text-sm font-semibold truncate">{address.hostel_name} · Room {address.room_number}</span>
        {address.is_default && (
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full leading-none shrink-0
            ${selected ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-600'}`}>
            Default
          </span>
        )}
      </span>
      {(displayName || displayPhone) && (
        <span className={`block text-xs truncate mt-1 ${selected ? 'text-white/80' : 'text-gray-600'}`}>
          {displayName}{displayPhone ? ` · ${displayPhone}` : ''}
        </span>
      )}
      {address.college && (
        <span className={`block text-xs truncate mt-0.5 ${selected ? 'text-white/60' : 'text-gray-400'}`}>
          {address.college}
        </span>
      )}
    </button>
  );
}

/* ── Full-screen slide picker (reused for hostel selection in Add form) ── */
const FullScreenPicker = ({ visible, onClose, title, subtitle, children }) => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (visible) setTimeout(() => setShow(true), 10);
    else setShow(false);
  }, [visible]);
  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[80] bg-white flex flex-col"
      style={{ transform: show ? 'translateX(0)' : 'translateX(100%)', transition: 'transform 0.35s cubic-bezier(0.32, 0.72, 0, 1)' }}>
      <div className="flex items-center gap-4 px-4 pt-12 pb-4 bg-black border-b border-gray-800">
        <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors">
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div>
          <h2 className="text-xl font-bold text-white">{title}</h2>
          {subtitle && <p className="text-sm text-gray-400">{subtitle}</p>}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">{children}</div>
    </div>
  );
};

const PickerItem = ({ label, sublabel, selected, onClick, icon }) => (
  <button type="button" onClick={onClick}
    className={`w-full flex items-center justify-between px-5 py-4 rounded-2xl border-2 transition-all text-left active:scale-[0.98]
      ${selected ? 'border-black bg-black text-white' : 'border-gray-100 bg-gray-50 text-gray-800 hover:border-gray-300'}`}>
    <div className="flex items-center gap-4">
      <span className={`w-10 h-10 rounded-xl flex items-center justify-center text-base font-bold
        ${selected ? 'bg-white/20 text-white' : 'bg-white text-gray-700 shadow-sm'}`}>{icon}</span>
      <div>
        <p className="font-semibold text-sm">{label}</p>
        {sublabel && <p className={`text-xs mt-0.5 ${selected ? 'text-white/70' : 'text-gray-400'}`}>{sublabel}</p>}
      </div>
    </div>
    {selected && (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
      </svg>
    )}
  </button>
);

const inputCls =
  'w-full px-3.5 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl outline-none ' +
  'focus:bg-white focus:ring-2 focus:ring-black focus:border-black transition-all text-gray-900';

const FieldLabel = ({ children, optional }) => (
  <label className="block text-xs font-semibold text-gray-600 mb-1.5">
    {children}{optional && <span className="text-gray-400 font-normal"> (optional)</span>}
  </label>
);

/* ── Add address form with full-screen pickers ── */
function AddAddressForm({ defaultCollege, onCancel, onSaved }) {
  const { addAddress, profile } = useCustomer();
  const [addressType, setAddressType] = useState('self');
  const [form, setForm] = useState({
    label: 'Hostel Room',
    recipientName: profile?.name || '',
    recipientPhone: profile?.phone || '',
    hostelType: '',
    hostelName: '',
    roomNumber: '',
    college: defaultCollege || 'SRM University AP',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activePicker, setActivePicker] = useState(null); // 'hostelType' | 'hostelName'

  const hostelList = useMemo(
    () => (form.hostelType === 'male' ? MALE_HOSTELS : form.hostelType === 'female' ? FEMALE_HOSTELS : []),
    [form.hostelType]
  );

  const set = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const handleTypeChange = (type) => {
    setAddressType(type);
    if (type === 'self') {
      setForm((p) => ({ ...p, recipientName: profile?.name || '', recipientPhone: profile?.phone || '' }));
    } else {
      setForm((p) => ({ ...p, recipientName: '', recipientPhone: '' }));
    }
  };

  const canSave = form.hostelType && form.hostelName && form.roomNumber.trim() && form.recipientName.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSave || saving) return;
    setSaving(true);
    setError('');
    try {
      await addAddress({
        label: form.label.trim() || 'Hostel Room',
        hostelType: form.hostelType,
        hostelName: form.hostelName,
        roomNumber: form.roomNumber.trim(),
        college: form.college.trim(),
        landmark: '',
        deliveryNotes: '',
        recipientName: form.recipientName.trim(),
        recipientPhone: form.recipientPhone.trim(),
        addressType
      });
      onSaved?.();
    } catch (err) {
      setError(err.message || 'Could not save. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-5 pt-1 pb-3 flex items-center gap-2 shrink-0">
        <button type="button" onClick={onCancel}
          className="h-8 w-8 -ml-1 rounded-full flex items-center justify-center hover:bg-gray-100 active:scale-95 transition" aria-label="Back">
          <ArrowLeft className="h-5 w-5 text-gray-700" />
        </button>
        <h3 className="text-base font-bold text-gray-900">Add New Address</h3>
      </div>

      <form onSubmit={handleSubmit} className="flex-1 min-h-0 flex flex-col">
        <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-4 space-y-4">
          {error && <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>}

          {/* Who is this for? */}
          <div>
            <FieldLabel>Who is this address for?</FieldLabel>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => handleTypeChange('self')}
                className={`py-2.5 rounded-xl text-sm font-semibold border-2 transition
                  ${addressType === 'self' ? 'border-black bg-black text-white' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                My Address
              </button>
              <button type="button" onClick={() => handleTypeChange('other')}
                className={`py-2.5 rounded-xl text-sm font-semibold border-2 transition
                  ${addressType === 'other' ? 'border-black bg-black text-white' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                Someone Else
              </button>
            </div>
          </div>

          {/* Recipient */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <FieldLabel>Name</FieldLabel>
              <input className={inputCls} value={form.recipientName} onChange={(e) => set('recipientName', e.target.value)} placeholder="Full name" />
            </div>
            <div>
              <FieldLabel>Phone</FieldLabel>
              <input className={inputCls} value={form.recipientPhone} onChange={(e) => set('recipientPhone', e.target.value)} placeholder="Phone" type="tel" maxLength={10} />
            </div>
          </div>

          {/* Hostel Type — full-screen picker style trigger */}
          <div>
            <FieldLabel>Hostel Type</FieldLabel>
            <div className="grid grid-cols-2 gap-2">
              {[{ v: 'male', t: 'Boys' }, { v: 'female', t: 'Girls' }].map(({ v, t }) => (
                <button key={v} type="button"
                  onClick={() => { setForm(p => ({ ...p, hostelType: v, hostelName: '' })); setActivePicker('hostelName'); }}
                  className={`py-3 rounded-xl text-sm font-semibold border-2 transition
                    ${form.hostelType === v ? 'border-black bg-black text-white' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                  {t} Hostel
                </button>
              ))}
            </div>
          </div>

          {/* Hostel Name — shown as selected, tappable to re-pick */}
          {form.hostelType && (
            <div>
              <FieldLabel>Hostel</FieldLabel>
              <button type="button" onClick={() => setActivePicker('hostelName')}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all
                  ${form.hostelName ? 'border-black bg-black text-white' : 'border-gray-200 text-gray-400 hover:border-gray-300 bg-gray-50'}`}>
                <span className="font-semibold text-sm">{form.hostelName || 'Tap to select hostel'}</span>
                <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}

          {/* Room Number */}
          <div>
            <FieldLabel>Room Number</FieldLabel>
            <input className={inputCls} value={form.roomNumber} onChange={(e) => set('roomNumber', e.target.value)} placeholder="e.g. 308" />
          </div>

          {/* College — dropdown */}
          <div>
            <FieldLabel>College</FieldLabel>
            <select className={inputCls} value={form.college} onChange={(e) => set('college', e.target.value)}>
              <option value="SRM University AP">SRM University AP</option>
              <option value="VIT University AP" disabled>VIT University AP (Coming Soon)</option>
            </select>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-gray-100 bg-white shrink-0">
          <button type="submit" disabled={!canSave || saving}
            className="w-full py-3 rounded-xl bg-black text-white text-sm font-semibold flex items-center justify-center gap-2
              disabled:bg-gray-200 disabled:text-gray-400 transition active:scale-[0.99]">
            {saving ? (<><Loader2 className="h-4 w-4 animate-spin" /> Saving...</>) : 'Save Address'}
          </button>
        </div>
      </form>

      {/* Full-screen hostel picker */}
      <FullScreenPicker
        visible={activePicker === 'hostelName'}
        onClose={() => setActivePicker(null)}
        title={form.hostelType === 'male' ? 'Boys Hostels' : 'Girls Hostels'}
        subtitle="Select your hostel block"
      >
        {hostelList.map(h => (
          <PickerItem key={h} label={h}
            sublabel={form.hostelType === 'male' ? 'Boys Block' : 'Girls Block'}
            selected={form.hostelName === h}
            icon={h.charAt(0)}
            onClick={() => { set('hostelName', h); setActivePicker(null); }} />
        ))}
      </FullScreenPicker>
    </div>
  );
}

/* ── Main address selector drawer ── */
export default function AddressDrawer() {
  const {
    addresses, addressesLoading, selectedAddress, selectAddress,
    mustSelectAddress, addressDrawerOpen, closeAddressDrawer, profile
  } = useCustomer();

  const [addOpen, setAddOpen] = useState(false);

  const open = mustSelectAddress || addressDrawerOpen;
  const dismissible = !mustSelectAddress;

  const handleSelect = (addr) => {
    // Tap to select immediately — no continue button needed
    selectAddress(addr);
  };

  const handleClose = () => { if (dismissible) closeAddressDrawer(); };

  const isEmpty = !addressesLoading && addresses.length === 0;

  return (
    <>
      <BottomSheet
        open={open && !addOpen}
        onClose={handleClose}
        dismissible={dismissible}
        heightClass="h-[55vh]"
        labelledBy="address-drawer-title"
      >
        {/* Header */}
        <div className="px-5 pt-1 pb-3 flex items-start justify-between shrink-0">
          <div>
            <h3 id="address-drawer-title" className="text-base font-bold text-gray-900">
              Select Delivery Address
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">Tap an address to deliver there.</p>
          </div>
          {dismissible && (
            <button type="button" onClick={handleClose}
              className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-gray-100 active:scale-95 transition" aria-label="Close">
              <X className="h-5 w-5 text-gray-500" />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-2">
          {addressesLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : isEmpty ? (
            <div className="flex flex-col items-center justify-center text-center px-4 py-8">
              <div className="h-14 w-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                <MapPin className="h-6 w-6 text-gray-400" />
              </div>
              <p className="text-sm font-semibold text-gray-900">No saved addresses</p>
              <p className="text-xs text-gray-500 mt-1 mb-4">Add your hostel room to continue.</p>
            </div>
          ) : (
            <div className="space-y-2.5 py-1">
              {addresses.map((addr) => (
                <AddressCard
                  key={addr.id}
                  address={addr}
                  profile={profile}
                  selected={selectedAddress?.id === addr.id}
                  onSelect={() => handleSelect(addr)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer — always shows Add New Address */}
        <div className="px-5 py-3 border-t border-gray-100 bg-white shrink-0">
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="w-full py-3 rounded-xl bg-black text-white text-sm font-semibold flex items-center justify-center gap-2 active:scale-[0.99] transition"
          >
            <Plus className="h-4 w-4" /> Add New Address
          </button>
        </div>
      </BottomSheet>

      {/* Add address sheet */}
      <BottomSheet open={addOpen} onClose={() => setAddOpen(false)} dismissible heightClass="h-[88vh]" labelledBy="add-address-title">
        <AddAddressForm
          defaultCollege={profile?.college}
          onCancel={() => setAddOpen(false)}
          onSaved={() => { setAddOpen(false); closeAddressDrawer(); }}
        />
      </BottomSheet>
    </>
  );
}
